import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import cn from 'classnames';
import { format as formatDate } from 'date-fns';
import { ArrowSolidDown } from '../../icons';
import { useDevice } from '../../providers/Device';
import { useBiAction } from '../BiTracking';
import { PickerLocalization } from '../DatePicker/PickerLocalization';
import { useOverlayPopupZIndex } from '../OverlayContext/OverlayPopupZIndex/OverlayPopupZIndex';
import styles from './TimePicker.module.scss';
import { DEFAULT_TIME_STEP_MINUTES, fieldFormatFor, GetTimePickerTID, isMinutesSection, slotFormatFor, steppedByMinutes, timeSlotsFor } from './TimePicker.utils';
export function TimePicker(props) {
    const { value, onChange, format = '12h', timeStep = DEFAULT_TIME_STEP_MINUTES, listPresentation = 'dropdown', minTime, maxTime, shouldDisableTime, label, helperText, error, errorText, disabled, testId, bi } = props;
    const { TimePickerTestIds } = useMemo(()=>GetTimePickerTID(testId), [
        testId
    ]);
    const { isMobileDevice } = useDevice();
    const logBiAction = useBiAction();
    const popupZIndex = useOverlayPopupZIndex();
    const anchorRef = useRef(null);
    // WHY: an anchored slot list, not a centered dialog — OverlayContext.showModal does not apply.
    // eslint-disable-next-line no-restricted-syntax
    const [open, setOpen] = useState(false);
    const referenceDay = useMemo(()=>new Date(0), []);
    const slots = useMemo(()=>timeSlotsFor({
            value,
            referenceDay,
            timeStep,
            minTime,
            maxTime,
            shouldDisableTime
        }), [
        value,
        referenceDay,
        timeStep,
        minTime,
        maxTime,
        shouldDisableTime
    ]);
    const slotFormat = slotFormatFor(format);
    const messageText = error === true && errorText != null ? errorText : helperText;
    const isInline = listPresentation === 'inline';
    // WHY: a ref callback fires when the row mounts; an effect on `open` runs before the portal children exist.
    const scrollSelectedIntoView = useCallback((node)=>{
        var _node_scrollIntoView;
        node === null || node === void 0 ? void 0 : (_node_scrollIntoView = node.scrollIntoView) === null || _node_scrollIntoView === void 0 ? void 0 : _node_scrollIntoView.call(node, {
            block: 'center'
        });
    }, []);
    const handleSlotClick = (slot)=>{
        setOpen(false);
        if ((bi === null || bi === void 0 ? void 0 : bi.name) != null) {
            logBiAction({
                entityType: 'time_picker',
                entityName: bi.name,
                actionType: 'select',
                testId,
                data: _object_spread_props(_object_spread({}, bi.data), {
                    selected_value: formatDate(slot, slotFormat)
                })
            });
        }
        onChange(slot);
    };
    // WHY: capture phase — MUI steps minutes by 1 and stops propagation, so a bubbling handler never runs.
    const handleKeyDownCapture = (event)=>{
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        if (value == null || !isMinutesSection(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        onChange(steppedByMinutes(value, event.key === 'ArrowUp' ? timeStep : -timeStep));
    };
    const slotList = /*#__PURE__*/ _jsx("div", {
        className: cn(styles.list, {
            [styles.listInline]: isInline
        }),
        "data-testid": TimePickerTestIds.List(),
        children: slots.map((slot)=>{
            const slotLabel = formatDate(slot, slotFormat);
            const selected = value != null && formatDate(value, slotFormat) === slotLabel;
            return /*#__PURE__*/ _jsx("button", {
                type: "button",
                ref: selected ? scrollSelectedIntoView : undefined,
                className: cn(styles.slot, {
                    [styles.slotSelected]: selected
                }),
                "data-testid": TimePickerTestIds.Slot(slotLabel),
                onClick: ()=>handleSlotClick(slot),
                children: slotLabel
            }, slotLabel);
        })
    });
    return /*#__PURE__*/ _jsx(PickerLocalization, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.root,
            "data-testid": TimePickerTestIds.Root(),
            ref: anchorRef,
            children: [
                isInline && label != null && /*#__PURE__*/ _jsx("span", {
                    className: styles.inlineLabel,
                    "data-testid": TimePickerTestIds.Label(),
                    children: label
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: cn(styles.field, {
                        [styles.fieldError]: error === true
                    }),
                    "data-testid": TimePickerTestIds.Field(),
                    children: [
                        /*#__PURE__*/ _jsx(TimeField, {
                            enableAccessibleFieldDOMStructure: true,
                            label: isInline ? undefined : label,
                            value: value,
                            onChange: onChange,
                            disabled: disabled,
                            readOnly: isMobileDevice,
                            format: fieldFormatFor(format),
                            onKeyDownCapture: handleKeyDownCapture,
                            slotProps: {
                                textField: {
                                    error,
                                    fullWidth: true
                                }
                            }
                        }),
                        !isInline && /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            className: styles.toggle,
                            "data-testid": TimePickerTestIds.Toggle(),
                            disabled: disabled,
                            "aria-label": label,
                            onClick: ()=>setOpen((wasOpen)=>!wasOpen),
                            children: /*#__PURE__*/ _jsx(ArrowSolidDown, {
                                size: 20
                            })
                        })
                    ]
                }),
                messageText != null && /*#__PURE__*/ _jsx("span", {
                    className: cn(styles.message, {
                        [styles.messageError]: error === true
                    }),
                    "data-testid": TimePickerTestIds.Message(),
                    children: messageText
                }),
                isInline ? slotList : /*#__PURE__*/ _jsx(Popper, {
                    open: open && disabled !== true,
                    anchorEl: anchorRef.current,
                    placement: "bottom-start",
                    className: styles.popper,
                    style: {
                        zIndex: popupZIndex
                    },
                    disablePortal: true,
                    children: /*#__PURE__*/ _jsx(ClickAwayListener, {
                        onClickAway: ()=>setOpen(false),
                        children: slotList
                    })
                })
            ]
        })
    });
}
